/**
 * SelectableTableBlockRenderer
 *
 * Renders an interactive `selectable_table` block: a client-side table with a
 * checkbox column. The user selects one or more rows and clicks Apply; the
 * selected rows are emitted back to the chat agent via `onUIAction`.
 *
 * Lifecycle mirrors the other interactive renderers (select / radio): staged
 * selection, then Apply emits a UIActionEvent and the element locks
 * (`setDisabled(true)`).
 *
 * Column/cell building reuses the shared data-table cell utilities
 * (`renderDataTableCellContent`, `makeSortingFn`, `unwrapCellValue`) — minus
 * page navigation, which has no meaning for a selection table. The block
 * content follows the documented `selectable_table` contract (keyed columns +
 * object rows from the prompt registry).
 */

import { useMemo, useState, useCallback } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { SelectableTable } from '@strivelabs/ui-blocks';
import type { UIBlock, UIActionEvent, DataTableClientRow } from '@/types/ui-blocks';
import {
  adaptWireTableContent,
  makeSortingFn,
  renderDataTableCellContent,
  unwrapCellValue,
} from './data-table-cell-utils';

export interface SelectableTableBlockRendererProps {
  block: UIBlock;
  onUIAction: (event: UIActionEvent) => void;
  /** When true, the component is disabled (post-submit / read-only history). */
  readOnly?: boolean;
}

/**
 * Stable id for a row: the `idKey` cell value when it's a usable primitive,
 * otherwise the row's position in the dataset.
 */
function resolveRowId(
  row: DataTableClientRow,
  index: number,
  idKey: string | undefined,
): string | number {
  if (idKey) {
    const v = row[idKey];
    if (v !== null && v !== undefined && typeof v !== 'object') return v as string | number;
  }
  return index;
}

export function SelectableTableBlockRenderer({
  block,
  onUIAction,
  readOnly = false,
}: SelectableTableBlockRendererProps) {
  // Derive the locked state from the `readOnly` prop OR a local lock set after
  // the user acts. `useState(readOnly)` would snapshot the prop only on mount,
  // so a later flip to read-only (history replay / turn going inactive) would
  // leave the table interactive. Combining the two keeps it correct either way.
  const [localDisabled, setLocalDisabled] = useState(false);
  const disabled = readOnly || localDisabled;

  // The chat path streams the wire shape (positional `cells` + `columns[].name`);
  // normalise to the legacy keyed shape the rest of this renderer consumes.
  const content = useMemo(
    () => (block.type === 'selectable_table' ? adaptWireTableContent(block.content) : null),
    [block],
  );

  // Build TanStack columns from the (adapted) contract columns.
  const columns = useMemo<ColumnDef<DataTableClientRow>[]>(() => {
    if (!content) return [];
    return content.columns.map((col) => ({
      accessorKey: col.key,
      header: col.label,
      enableSorting: col.enableSorting ?? false,
      ...(col.enableSorting && col.sortType ? { sortingFn: makeSortingFn(col.sortType) } : {}),
      cell: ({ row }) => renderDataTableCellContent(row.original[col.key]),
    }));
  }, [content]);

  const idKey = content?.idKey;
  const getRowId = useCallback(
    (row: DataTableClientRow, index: number): string => String(resolveRowId(row, index, idKey)),
    [idKey],
  );

  const handleApply = useCallback(
    (selectedRows: DataTableClientRow[]) => {
      if (disabled || !content) return;

      const rowIds = selectedRows.map((row) => resolveRowId(row, content.rows.indexOf(row), idKey));

      const rows = selectedRows.map((row) => {
        const out: Record<string, string | number | null> = {};
        content.columns.forEach((col) => {
          out[col.key] = unwrapCellValue(row[col.key]);
        });
        return out;
      });

      onUIAction({
        type: 'ui_action',
        component: 'selectable_table',
        label: content.label ?? content.title,
        state: { rowIds, rows, confirmed: true },
      });

      setLocalDisabled(true);
    },
    [disabled, content, idKey, onUIAction],
  );

  const handleCancel = useCallback(() => {
    if (disabled) return;
    setLocalDisabled(true);
  }, [disabled]);

  if (!content) return null;

  return (
    <SelectableTable<DataTableClientRow>
      columns={columns}
      data={content.rows}
      getRowId={getRowId}
      title={content.title ?? content.label}
      description={content.description}
      enableSearch={content.enableSearch ?? false}
      searchPlaceholder={content.searchPlaceholder}
      enablePagination={content.enablePagination ?? false}
      pageSize={content.pageSize ?? 10}
      emptyText={content.emptyText ?? 'No results found'}
      minSelected={content.minSelected ?? 1}
      applyButtonText={content.applyButtonText}
      cancelButtonText={content.cancelButtonText}
      disabled={disabled}
      onApply={handleApply}
      onCancel={handleCancel}
    />
  );
}
